import { Box, Card, CardContent, Typography, Button } from '@mui/material';
import { CodeOutlined, OpenInNew } from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import OrganizationContentStack from '@/components/organization-content-stack';
import PageHeaderBar from '@/components/common/page-header-bar';
import { useAuth } from '@clerk/clerk-react';
import { useState } from 'react';
import { useNotifications } from '@/hooks/use-notifications';

interface DocsCodeResponse {
  code: string;
  expiresAt: string;
}

export default function SwaggerPage() {
  const t = useTranslations();
  const { getToken } = useAuth();
  const notification = useNotifications();
  const [isLoading, setIsLoading] = useState(false);

  const handleOpenSwagger = async () => {
    setIsLoading(true);
    try {
      const token = await getToken();
      if (!token) {
        notification.notifyError(t('admin.swagger.error') || 'Authentication required. Please log in.');
        setIsLoading(false);
        return;
      }

      const apiUrl = import.meta.env.VITE_API_URL || '';
      // Remove /v1 suffix if present to get base URL, since /docs-code is not versioned
      const baseUrl = apiUrl.replace(/\/v1$/, '');

      // Fetch one-time code from /docs-code endpoint (not versioned)
      const response = await fetch(`${baseUrl}/docs-code`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to fetch docs code:', response.status, errorText);
        notification.notifyError(
          t('admin.swagger.fetchCodeError') || 'Failed to get access code. Please ensure you have admin permissions.',
        );
        setIsLoading(false);
        return;
      }

      const data: DocsCodeResponse = await response.json();

      if (!data.code) {
        notification.notifyError(
          t('admin.swagger.invalidCodeError') || 'Invalid response from server. Please try again.',
        );
        setIsLoading(false);
        return;
      }

      // Open Swagger UI with the one-time code
      // Backend will exchange the code for a cookie and redirect to /docs
      // Use baseUrl (without /v1) since /docs is not versioned
      const swaggerUrl = `${baseUrl}/docs?code=${encodeURIComponent(data.code)}`;
      window.open(swaggerUrl, '_blank', 'noopener,noreferrer');
      setIsLoading(false);
    } catch (error) {
      console.error('Error opening Swagger:', error);
      notification.notifyError(
        t('admin.swagger.error') || 'Error loading Swagger documentation. Make sure the backend server is running.',
      );
      setIsLoading(false);
    }
  };

  return (
    <OrganizationContentStack
      content={
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <PageHeaderBar
            mainContent={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CodeOutlined sx={{ fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('admin.swagger') || 'Swagger API Docs'}
                </Typography>
              </Box>
            }
          />

          <Card
            sx={{
              mt: 2,
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <CardContent
              sx={{
                'flex': 1,
                'minHeight': 0,
                'display': 'flex',
                'flexDirection': 'column',
                'overflow': 'hidden',
                'p': 0,
                '&:last-child': {
                  pb: 0,
                },
              }}
            >
              <Box
                sx={{
                  p: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  height: '100%',
                }}
              >
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {t('admin.swagger.openInNewTab') ||
                    'Click the button below to open Swagger API documentation in a new tab.'}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<OpenInNew />}
                  onClick={handleOpenSwagger}
                  size="large"
                  disabled={isLoading}
                >
                  {isLoading
                    ? t('common.loading') || 'Loading...'
                    : t('admin.swagger.openSwagger') || 'Open Swagger Documentation'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      }
    />
  );
}
